import { Injectable, InjectionToken, Provider } from '@angular/core';
import { mockDisabilityProofs } from '../../mock-data/disability-proof.mock';
import { PaginatedResponse } from '../../shared/pagination/paginated-response';
import { buildPaginationQuery } from '../../shared/pagination/pagination.builder';
import {
	ApiDisabilityProof,
	ApiDisabilityProofCreate,
	ApiDisabilityProofUpdateBody,
} from '../api-types/disability-proof.api';
import { ApiDisabilityStatus } from '../api-types/disability-status.api';
import { DisabilityProof } from '../app-types/disability-proof.app';
import {
	transformApiDisabilityProofNeighborsToAppDisabilityProofNeighbors,
	transformApiDisabilityProofsToAppDisabilityProofs,
	transformApiDisabilityProofToAppDisabilityProof,
} from '../transformers/disability-proof.transform';
import { transformApiDisabilityStatusToAppDisabilityStatus } from '../transformers/disability-status.transform';
import { BaseRequestService } from './base-request.service';
import { ApiResponse, Metadata } from '../api-types/api-response.api';
import { NeighborContext } from '../app-types/neighbors.app';
import { ApiNeighborContext } from '../api-types/neighbor.api';

export const DISABILITY_PROOF_SERVICE = new InjectionToken<DisabilityProofService>('DISABILITY_PROOF_SERVICE');

@Injectable()
export class DisabilityProofService extends BaseRequestService {
	// This service will handle operations related to disability proofs
	// For example, fetching, creating, updating, and deleting disability proofs
	// The actual implementation will depend on the backend API and requirements

	public getAll = async (clientId: number, offset: number, range: number) => {
		const apiResponse = await this.get<PaginatedResponse<ApiDisabilityProof>>(
			`clients/${clientId}/disabilityreport`,
			buildPaginationQuery(offset, range),
		);
		const proofs = transformApiDisabilityProofsToAppDisabilityProofs(apiResponse.entities);
		return {
			entities: proofs,
			offset: apiResponse.offset,
			range: apiResponse.range,
			totalEntities: apiResponse.totalEntities,
		} as PaginatedResponse<DisabilityProof>;
	};

	public getDisabilityStatus = async (offset: number, range: number) => {
		try {
			this.runningRequests.update((requests) => [...requests, 'getAllDisabilityStatuses']);
			const apiResponse = await this.get<PaginatedResponse<ApiDisabilityStatus>>(
				`categories/disabilitystatus`,
				buildPaginationQuery(offset, range),
			);
			return transformApiDisabilityStatusToAppDisabilityStatus(apiResponse);
		} finally {
			// Remove the request from the running requests
			this.runningRequests.update((requests) => requests.filter((request) => request !== 'getAllDisabilityProofs'));
		}
	};

	public getSingle = async (clientId: number, disabilityProofId: number) => {
		const apiResponse = await this.get<ApiResponse<ApiDisabilityProof, Metadata>>(
			`clients/${clientId}/disabilityReport/${disabilityProofId}`,
		);
		const proofs = transformApiDisabilityProofToAppDisabilityProof(apiResponse.data);
		return { data: proofs, metadata: apiResponse.metadata };
	};

	public create = async (data: ApiDisabilityProofCreate) => {
		const apiResponse = await this.post<ApiDisabilityProof, ApiDisabilityProofCreate>(`disabilityReports`, data);
		const proof = transformApiDisabilityProofToAppDisabilityProof(apiResponse);
		return proof;
	};

	public update = async (body: ApiDisabilityProofUpdateBody) => {
		const response = await this.put<ApiDisabilityProofUpdateBody, ApiDisabilityProofUpdateBody>(
			`disabilityReports`,
			body,
		);
		return response;
	};

	public delete = async (clientId: number, reportId: number) => {
		await this.remove<void>(`disabilityReports/${clientId}/${reportId}`);
	};

	public async getProofNeighbors(
		clientId: number | string,
		disabilityProofId: number | string,
	): Promise<NeighborContext<DisabilityProof>> {
		const response = await this.get<ApiNeighborContext<ApiDisabilityProof>>(
			`disabilityReports/${clientId}/${disabilityProofId}/neighbors`,
		);
		return transformApiDisabilityProofNeighborsToAppDisabilityProofNeighbors(response);
	}
}

@Injectable()
export class DisabilityProofMockService extends BaseRequestService {
	public constructor() {
		super();
		console.warn('⛔️⛔️⛔️ Disability Proof Mock Service: You are currently using a mock service!');
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public getAll = async (clientId: number, offset: number, range: number) => {
		return Promise.resolve(mockDisabilityProofs);
	};

	public getSingle = async (disabilityProofId: number) => {
		const proof = mockDisabilityProofs.find((p) => p.id === disabilityProofId);
		return Promise.resolve(proof);
	};
}

export const provideDisabilityProofApi: Provider = {
	provide: DISABILITY_PROOF_SERVICE,
	useClass: DisabilityProofService,
};
